/**
 * 渲染器。
 *
 * 与模拟层完全解耦：模拟以 20 Hz 推进，渲染以显示器刷新率运行，
 * 中间用「上一帧 / 当前帧」两份快照做线性插值（技术文档 §6.3）。
 *
 * 图层自下而上：
 *   天空 → 地形（一次性烘焙）→ 矿脉 → 裂隙 → 建造预览 → 实体 → 粒子 → 血条 → 战争迷雾 → 法则滤镜
 */

import {
  Application,
  Container,
  Sprite,
  Texture,
  Graphics,
  Matrix,
} from 'pixi.js';
import {
  MAP_W,
  MAP_H,
  SNAP_STRIDE,
  Snap,
  SnapFlag,
  Kind,
  Fog,
  PLAYER_HUMAN,
  Damage,
} from '../shared/constants.ts';
import { defOf } from '../sim/data/types.ts';
import { toFloat } from '../sim/fixed/fix.ts';
import {
  TILE_W,
  TILE_H,
  HALF_W,
  HALF_H,
  tileToWorldX,
  tileToWorldY,
  worldToTileX,
  worldToTileY,
  LAYER_MATRIX,
} from './iso.ts';
import {
  getSprite,
  getProjectileSprite,
  makeFootprintTexture,
  bakeAllSprites,
  dirFromVector,
  WALK_FRAMES,
  hasRecoilAnim,
  hasDriveAnim,
} from './sprites.ts';
import { Camera } from './camera.ts';
import { bakeTerrain, prefetchTerrainStamps } from './map/terrainBake.ts';
import { ParticleFx } from './fx/particles.ts';
import { LawEffects } from './fx/lawEffects.ts';

/** 单个实体的渲染态：保存上一帧与当前帧位置用于插值 */
interface EntView {
  sprite: Sprite;
  trail: Sprite;
  prevX: number;
  prevY: number;
  curX: number;
  curY: number;
  trailX: number;
  trailY: number;
  hp: number;
  maxHp: number;
  owner: number;
  typeId: number;
  kind: number;
  flags: number;
  progress: number;
  seen: number;
  dir: number;
  cargo: boolean;
  firing: boolean;
  moving: boolean;
  walkPhase: number;
  animKey: number;
  revealed: boolean;
}

export class Renderer {
  readonly app = new Application();
  readonly camera = new Camera();

  private world = new Container();
  private sky = new Graphics();
  private terrainSprite!: Sprite;
  private oreSprite!: Sprite;
  private riftLayer = new Container();
  private ghostLayer = new Container();
  private entityLayer = new Container();
  private overlay = new Graphics();
  private fogSprite!: Sprite;
  private uiLayer = new Container();
  private selectionBox = new Graphics();
  private fx = new ParticleFx();
  private laws = new LawEffects();

  private views = new Map<number, EntView>();
  private spritePool: Sprite[] = [];
  private frameStamp = 0;
  private time = 0;

  /** 96×96 的地形缩略图，供小地图以「一次 drawImage + 仿射变换」的方式绘制 */
  minimapTerrain!: HTMLCanvasElement;
  private oreCanvas!: HTMLCanvasElement;
  private oreCtx!: CanvasRenderingContext2D;
  private oreTexture!: Texture;
  private fogCanvas!: HTMLCanvasElement;
  private fogCtx!: CanvasRenderingContext2D;
  private fogTexture!: Texture;
  private fogData!: ImageData;
  private fogGrid = new Uint8Array(MAP_W * MAP_H);

  private ghost: Sprite | null = null;
  private ghostModel: Sprite | null = null;
  private ghostOkTex = new Map<number, Texture>();
  private ghostBadTex = new Map<number, Texture>();

  private riftPulse: Graphics[] = [];

  /** 插值进度 0..1，由主循环根据距上次快照的时间推进 */
  alpha = 1;
  fps = 0;
  private fpsAccum = 0;
  private fpsFrames = 0;

  async init(canvas: HTMLCanvasElement): Promise<void> {
    await this.app.init({
      canvas,
      resizeTo: window,
      background: 0x1a1712,
      antialias: false,
      preference: 'webgl',
      resolution: Math.min(2, window.devicePixelRatio || 1),
      autoDensity: true,
    });

    bakeAllSprites();
    prefetchTerrainStamps();

    this.world.addChild(this.riftLayer);
    this.world.addChild(this.ghostLayer);
    this.world.addChild(this.entityLayer);
    this.world.addChild(this.fx.layer);
    this.world.addChild(this.overlay);
    this.entityLayer.sortableChildren = true;
    this.fx.layer.sortableChildren = false;

    this.app.stage.addChild(this.sky);
    this.app.stage.addChild(this.world);
    this.laws.overlay.eventMode = 'none';
    this.sky.eventMode = 'none';
    this.app.stage.addChild(this.laws.overlay);
    this.uiLayer.addChild(this.selectionBox);
    this.app.stage.addChild(this.uiLayer);

    this.camera.resize(this.app.screen.width, this.app.screen.height);
    window.addEventListener('resize', () => {
      this.camera.resize(this.app.screen.width, this.app.screen.height);
    });
  }

  resetWorld(): void {
    for (const v of this.views.values()) {
      this.entityLayer.removeChild(v.sprite);
      this.entityLayer.removeChild(v.trail);
      v.sprite.visible = false;
      v.trail.visible = false;
      this.spritePool.push(v.sprite);
      this.spritePool.push(v.trail);
    }
    this.views.clear();
  }

  // ── 地形烘焙 ────────────────────────────────────────────

  buildTerrain(
    tiles: Uint8Array,
    rifts: Array<{ x: number; y: number }>,
    bases: Array<{ x: number; y: number }> = [],
  ): void {
    // 重开一局时清掉旧地形
    if (this.terrainSprite) {
      this.world.removeChild(this.terrainSprite);
      this.terrainSprite.destroy();
    }
    if (this.oreSprite) {
      this.world.removeChild(this.oreSprite);
      this.oreSprite.destroy();
    }
    if (this.fogSprite) {
      this.world.removeChild(this.fogSprite);
      this.fogSprite.destroy();
    }
    this.riftLayer.removeChildren();
    this.riftPulse = [];

    const baked = bakeTerrain(tiles, rifts, bases);
    this.minimapTerrain = baked.minimap;

    this.terrainSprite = new Sprite(Texture.from(baked.canvas));
    this.terrainSprite.scale.set(1 / baked.scale);
    this.terrainSprite.position.set(-baked.originX, 0);
    this.world.addChildAt(this.terrainSprite, 0);

    this.oreCanvas = document.createElement('canvas');
    this.oreCanvas.width = MAP_W;
    this.oreCanvas.height = MAP_H;
    this.oreCtx = this.oreCanvas.getContext('2d')!;
    this.oreTexture = Texture.from(this.oreCanvas);
    this.oreTexture.source.scaleMode = 'nearest';
    this.oreSprite = new Sprite(this.oreTexture);
    this.applyLayerMatrix(this.oreSprite);
    this.world.addChildAt(this.oreSprite, 1);

    this.fogCanvas = document.createElement('canvas');
    this.fogCanvas.width = MAP_W;
    this.fogCanvas.height = MAP_H;
    this.fogCtx = this.fogCanvas.getContext('2d')!;
    this.fogData = this.fogCtx.createImageData(MAP_W, MAP_H);
    this.fogTexture = Texture.from(this.fogCanvas);
    this.fogTexture.source.scaleMode = 'nearest';
    this.fogSprite = new Sprite(this.fogTexture);
    this.applyLayerMatrix(this.fogSprite);
    this.world.addChild(this.fogSprite);

    for (const r of rifts) {
      const g = new Graphics();
      const wx = tileToWorldX(r.x + 0.5, r.y + 0.5);
      const wy = tileToWorldY(r.x + 0.5, r.y + 0.5);
      this.riftLayer.addChild(g);
      this.riftPulse.push(g);
      g.position.set(wx, wy);
    }
  }

  private applyLayerMatrix(s: Sprite): void {
    const m = LAYER_MATRIX;
    s.setFromMatrix(new Matrix(m.a, m.b, m.c, m.d, m.tx, m.ty));
  }

  updateOre(ore: Int32Array): void {
    if (!this.oreCtx) return;
    const img = this.oreCtx.createImageData(MAP_W, MAP_H);
    const d = img.data;
    for (let i = 0; i < MAP_W * MAP_H; i++) {
      const amt = ore[i]!;
      const o = i * 4;
      if (amt <= 0) {
        d[o + 3] = 0;
        continue;
      }
      const f = Math.min(1, amt / 1400);
      d[o] = 220 + ((f * 35) | 0);
      d[o + 1] = 150 + ((f * 60) | 0);
      d[o + 2] = 40 + ((f * 30) | 0);
      d[o + 3] = (40 + f * 90) | 0;
    }
    this.oreCtx.putImageData(img, 0, 0);
    this.oreTexture.source.update();
  }

  updateFog(fog: Uint8Array): void {
    this.fogGrid.set(fog);
    if (!this.fogCtx) return;
    const d = this.fogData.data;
    for (let i = 0; i < fog.length; i++) {
      const o = i * 4;
      const v = fog[i]!;
      d[o] = 8;
      d[o + 1] = 7;
      d[o + 2] = 6;
      d[o + 3] = v === Fog.Visible ? 0 : v === Fog.Explored ? 140 : 255;
    }
    this.fogCtx.putImageData(this.fogData, 0, 0);
    this.fogTexture.source.update();
  }

  private fogAt(tx: number, ty: number): number {
    if (tx < 0 || ty < 0 || tx >= MAP_W || ty >= MAP_H) return Fog.Unseen;
    return this.fogGrid[ty * MAP_W + tx]!;
  }

  /** 敌方只在当前视野内显示；己方在已探明区域也显示 */
  isRevealed(owner: number, wx: number, wy: number, typeId: number): boolean {
    if (owner === PLAYER_HUMAN) return this.fogAt(Math.floor(worldToTileX(wx, wy)), Math.floor(worldToTileY(wx, wy))) !== Fog.Unseen;
    const def = defOf(typeId);
    if (def.kind === Kind.Building) {
      const fp = def.footprint;
      const tx0 = Math.floor(worldToTileX(wx, wy) - fp / 2);
      const ty0 = Math.floor(worldToTileY(wx, wy) - fp / 2);
      for (let dy = 0; dy < fp; dy++) {
        for (let dx = 0; dx < fp; dx++) {
          if (this.fogAt(tx0 + dx, ty0 + dy) === Fog.Visible) return true;
        }
      }
      return false;
    }
    return this.fogAt(Math.floor(worldToTileX(wx, wy)), Math.floor(worldToTileY(wx, wy))) === Fog.Visible;
  }

  // ── 快照接入 ────────────────────────────────────────────

  applySnapshot(ents: Int32Array, count: number): void {
    this.frameStamp++;
    for (let i = 0; i < count; i++) {
      const o = i * SNAP_STRIDE;
      const id = ents[o + Snap.Id]!;
      const wx = tileToWorldX(toFloat(ents[o + Snap.X]!), toFloat(ents[o + Snap.Y]!));
      const wy = tileToWorldY(toFloat(ents[o + Snap.X]!), toFloat(ents[o + Snap.Y]!));
      const flags = ents[o + Snap.Flags]!;
      const dirX = toFloat(ents[o + Snap.DirX]!);
      const dirY = toFloat(ents[o + Snap.DirY]!);
      const cargo = (flags & SnapFlag.Cargo) !== 0;
      const firing = (flags & SnapFlag.Firing) !== 0;
      let dir = dirFromVector(dirX, dirY);
      if (dirX === 0 && dirY === 0) dir = -1;

      let v = this.views.get(id);
      if (!v) {
        const typeId = ents[o + Snap.TypeId]!;
        const owner = ents[o + Snap.Owner]!;
        const kind = ents[o + Snap.Kind]!;
        const sprite = this.takeSprite();
        const trail = this.takeSprite();
        const face = dir < 0 ? 0 : dir;
        const baked =
          kind === Kind.Projectile
            ? getProjectileSprite(defOf(typeId).dmgType, face)
            : getSprite(typeId, owner, face, cargo);
        sprite.texture = baked.texture;
        sprite.anchor.set(baked.anchorX, baked.anchorY);
        sprite.visible = true;
        sprite.alpha = 1;
        trail.texture = baked.texture;
        trail.anchor.set(baked.anchorX, baked.anchorY);
        trail.visible = false;
        trail.alpha = 0.28;
        this.entityLayer.addChild(sprite);
        this.entityLayer.addChild(trail);
        v = {
          sprite,
          trail,
          prevX: wx,
          prevY: wy,
          curX: wx,
          curY: wy,
          trailX: wx,
          trailY: wy,
          hp: 0,
          maxHp: 1,
          owner,
          typeId,
          kind,
          flags: 0,
          progress: 1000,
          seen: this.frameStamp,
          dir: face,
          cargo,
          firing: false,
          moving: false,
          walkPhase: (wx + wy) * 0.01,
          animKey: -1,
          revealed: true,
        };
        this.views.set(id, v);
      }

      v.trailX = v.prevX;
      v.trailY = v.prevY;
      v.prevX = v.curX;
      v.prevY = v.curY;
      v.curX = wx;
      v.curY = wy;
      v.hp = ents[o + Snap.Hp]!;
      v.maxHp = ents[o + Snap.MaxHp]!;
      v.flags = flags;
      v.progress = ents[o + Snap.Progress]!;
      v.seen = this.frameStamp;
      v.moving = Math.hypot(v.curX - v.prevX, v.curY - v.prevY) > 0.4;

      if (dir >= 0) v.dir = dir;
      v.cargo = cargo;
      v.revealed = this.isRevealed(v.owner, wx, wy, v.typeId);

      if (firing && !v.firing && v.kind !== Kind.Projectile && v.revealed) {
        this.fx.muzzle(wx, wy - 12, dirX === 0 && dirY === 0 ? 0.4 : dirX - dirY, dirX + dirY);
      }
      v.firing = firing;
    }

    for (const [id, v] of this.views) {
      if (v.seen === this.frameStamp) continue;
      if (v.kind === Kind.Projectile) {
        if (v.revealed) {
          const dmg = defOf(v.typeId).dmgType;
          const power = dmg === Damage.Explosive ? 0.7 : dmg === Damage.AP ? 0.28 : 0.4;
          this.fx.impact(v.curX, v.curY - 10, power);
        }
      } else if (v.revealed) {
        const power = v.kind === Kind.Building ? 1.6 : v.kind === Kind.Infantry ? 0.55 : 1;
        this.fx.explosion(v.curX, v.curY - 8, power);
      }
      this.entityLayer.removeChild(v.sprite);
      this.entityLayer.removeChild(v.trail);
      v.sprite.visible = false;
      v.trail.visible = false;
      this.spritePool.push(v.sprite);
      this.spritePool.push(v.trail);
      this.views.delete(id);
    }
    this.alpha = 0;
  }

  private bindSprite(v: EntView, frame: number): void {
    if (v.kind === Kind.Projectile) {
      const key = 0x20000 | (v.dir & 7);
      if (key === v.animKey) return;
      v.animKey = key;
      const baked = getProjectileSprite(defOf(v.typeId).dmgType, v.dir);
      v.sprite.texture = baked.texture;
      v.sprite.anchor.set(baked.anchorX, baked.anchorY);
      v.trail.texture = baked.texture;
      v.trail.anchor.set(baked.anchorX, baked.anchorY);
      return;
    }
    const key = (v.dir << 8) | (frame << 1) | (v.cargo ? 1 : 0);
    if (key === v.animKey) return;
    v.animKey = key;
    const baked = getSprite(v.typeId, v.owner, v.dir, v.cargo, frame);
    v.sprite.texture = baked.texture;
    v.sprite.anchor.set(baked.anchorX, baked.anchorY);
    v.trail.texture = baked.texture;
    v.trail.anchor.set(baked.anchorX, baked.anchorY);
  }

  private takeSprite(): Sprite {
    return this.spritePool.pop() ?? new Sprite();
  }

  // ── 每帧绘制 ────────────────────────────────────────────

  draw(dtSec: number, lawId: number, buildGhost: { typeId: number; tx: number; ty: number; ok: boolean } | null): void {
    this.time += dtSec;
    this.camera.update(dtSec);
    this.laws.update(dtSec, lawId, this.app.screen.width, this.app.screen.height);

    const shake = this.laws.shake;
    this.world.position.set(this.camera.stageX + shake.x, this.camera.stageY + shake.y);
    this.world.scale.set(this.camera.zoom);

    this.drawSky();
    this.pulseRifts();

    const a = Math.min(1, this.alpha);
    this.overlay.clear();

    const halfW = this.camera.viewW / (2 * this.camera.zoom) + TILE_W * 3;
    const halfH = this.camera.viewH / (2 * this.camera.zoom) + TILE_H * 8;
    const minX = this.camera.x - halfW;
    const maxX = this.camera.x + halfW;
    const minY = this.camera.y - halfH;
    const maxY = this.camera.y + halfH;

    const afterimage = lawId === 10;

    for (const v of this.views.values()) {
      const x = v.prevX + (v.curX - v.prevX) * a;
      const y = v.prevY + (v.curY - v.prevY) * a;

      if (x < minX || x > maxX || y < minY || y > maxY) {
        v.sprite.visible = false;
        v.trail.visible = false;
        continue;
      }
      v.revealed = this.isRevealed(v.owner, x, y, v.typeId);
      if (!v.revealed) {
        v.sprite.visible = false;
        v.trail.visible = false;
        continue;
      }
      v.sprite.visible = true;
      let drawY = y;
      let frame = 0;
      if (v.kind === Kind.Projectile) {
        drawY = y - 10;
        if (Math.random() < 0.4) this.fx.tracer(x, drawY);
      } else if (v.kind === Kind.Infantry) {
        if (v.moving) {
          v.walkPhase += dtSec * 2.35;
          const cycle = v.walkPhase - Math.floor(v.walkPhase);
          frame = 1 + ((cycle * WALK_FRAMES) | 0);
          drawY = y - Math.abs(Math.sin(cycle * Math.PI * 2)) * 2.4;
        } else {
          v.walkPhase = 0;
          frame = 0;
        }
      } else if (hasRecoilAnim(v.typeId) && v.firing) {
        frame = 1;
      } else if (hasDriveAnim(v.typeId) && v.moving) {
        v.walkPhase += dtSec * 5.5;
        const cycle = v.walkPhase - Math.floor(v.walkPhase);
        frame = 2 + ((cycle * 2) | 0);
        drawY = y - Math.abs(Math.sin(cycle * Math.PI * 2)) * 1.05;
      } else if (v.moving && v.kind !== Kind.Building) {
        drawY = y - Math.abs(Math.sin(this.time * 13 + v.curX * 0.08)) * 1.1;
      }
      this.bindSprite(v, frame);
      v.sprite.position.set(x, drawY);
      v.sprite.zIndex = y * 10 + (v.kind === Kind.Projectile ? 5000 : 0);

      if (v.kind === Kind.Projectile) {
        v.trail.visible = true;
        v.trail.position.set(v.trailX, v.trailY - 10);
        v.trail.zIndex = v.sprite.zIndex - 1;
        v.trail.alpha = 0.4;
      } else if (afterimage) {
        v.trail.visible = true;
        v.trail.position.set(v.trailX, v.trailY);
        v.trail.zIndex = v.sprite.zIndex - 1;
        v.trail.alpha = 0.3;
      } else {
        v.trail.visible = false;
      }

      if ((v.flags & SnapFlag.Constructing) !== 0) {
        v.sprite.alpha = 0.38 + (v.progress / 1000) * 0.55;
      } else if (v.sprite.alpha !== 1) {
        v.sprite.alpha = 1;
      }

      if (v.moving && v.kind !== Kind.Projectile && v.kind !== Kind.Building && Math.random() < 0.08) {
        this.fx.dust(x, y);
      }

      const selected = (v.flags & SnapFlag.Selected) !== 0;
      if (selected) {
        const def = defOf(v.typeId);
        const rw = Math.max(0.7, def.kind === Kind.Building ? def.footprint * 0.62 : def.radius * 1.7);
        this.overlay.ellipse(x, y + 2, rw * HALF_W, rw * HALF_H);
        this.overlay.stroke({ color: v.owner === PLAYER_HUMAN ? 0x8ef0b0 : 0xe07060, width: 2 / this.camera.zoom, alpha: 0.95 });
        this.overlay.ellipse(x, y + 2, rw * HALF_W * 0.82, rw * HALF_H * 0.82);
        this.overlay.stroke({ color: 0xffffff, width: 1 / this.camera.zoom, alpha: 0.25 });
      }

      const hurt = v.hp < v.maxHp;
      if ((hurt || selected) && v.kind !== Kind.Projectile) {
        const def = defOf(v.typeId);
        const bw = def.kind === Kind.Building ? def.footprint * 26 : 22;
        const off = def.kind === Kind.Building ? 34 + def.footprint * 14 : v.kind === Kind.Infantry ? 36 : 32;
        const pct = Math.max(0, Math.min(1, v.hp / v.maxHp));
        this.overlay.rect(x - bw / 2, y - off, bw, 4);
        this.overlay.fill({ color: 0x000000, alpha: 0.7 });
        this.overlay.rect(x - bw / 2, y - off, bw * pct, 4);
        this.overlay.fill({
          color: pct > 0.6 ? 0x5fd97a : pct > 0.3 ? 0xe8c34a : 0xe05b4a,
        });
      }

      if ((v.flags & SnapFlag.Constructing) !== 0) {
        const def = defOf(v.typeId);
        const bw = def.footprint * 26;
        const off = 22 + def.footprint * 12;
        this.overlay.rect(x - bw / 2, y - off, bw, 3);
        this.overlay.fill({ color: 0x000000, alpha: 0.6 });
        this.overlay.rect(x - bw / 2, y - off, (bw * v.progress) / 1000, 3);
        this.overlay.fill({ color: 0x4fa3d1 });
      }
    }

    this.drawGhost(buildGhost);
    this.fx.lawAmbient(lawId, this.camera.x, this.camera.y, this.camera.viewW, this.camera.viewH, this.camera.zoom);
    this.fx.update(dtSec);

    this.fpsAccum += dtSec;
    this.fpsFrames++;
    if (this.fpsAccum >= 0.5) {
      this.fps = Math.round(this.fpsFrames / this.fpsAccum);
      this.fpsAccum = 0;
      this.fpsFrames = 0;
    }
  }

  private drawSky(): void {
    const g = this.sky;
    const w = this.app.screen.width;
    const h = this.app.screen.height;
    g.clear();
    g.rect(0, 0, w, h);
    g.fill({ color: 0x1c1914 });
    g.rect(0, 0, w, h * 0.42);
    g.fill({ color: 0x2a241c, alpha: 0.9 });
    // 晒白的废土太阳
    const sx = w * 0.18;
    const sy = h * 0.12;
    g.circle(sx, sy, 38);
    g.fill({ color: 0xe8c070, alpha: 0.18 });
    g.circle(sx, sy, 14);
    g.fill({ color: 0xf0d090, alpha: 0.35 });
  }

  private pulseRifts(): void {
    const k = 0.55 + 0.45 * Math.sin(this.time * 1.8);
    for (const g of this.riftPulse) {
      g.clear();
      for (let i = 4; i >= 1; i--) {
        g.ellipse(0, 0, i * HALF_W * 1.25, i * HALF_H * 1.25);
        g.fill({ color: 0x8a64d4, alpha: 0.05 * k });
      }
      g.ellipse(0, 0, HALF_W * 0.9, HALF_H * 0.9);
      g.fill({ color: 0xd0b4ff, alpha: 0.28 + 0.22 * k });
      g.ellipse(0, 0, HALF_W * 0.28, HALF_H * 0.28);
      g.fill({ color: 0xf4e8ff, alpha: 0.5 + 0.3 * k });
    }
  }

  private drawGhost(g: { typeId: number; tx: number; ty: number; ok: boolean } | null): void {
    if (!g) {
      if (this.ghost) this.ghost.visible = false;
      if (this.ghostModel) this.ghostModel.visible = false;
      return;
    }
    const def = defOf(g.typeId);
    const fp = def.footprint;
    const map = g.ok ? this.ghostOkTex : this.ghostBadTex;
    let tex = map.get(fp);
    if (!tex) {
      tex = makeFootprintTexture(fp, g.ok);
      map.set(fp, tex);
    }
    if (!this.ghost) {
      this.ghost = new Sprite();
      this.ghost.anchor.set(0.5, 0.5);
      this.ghostLayer.addChild(this.ghost);
    }
    if (!this.ghostModel) {
      this.ghostModel = new Sprite();
      this.ghostLayer.addChild(this.ghostModel);
    }
    const baked = getSprite(g.typeId, PLAYER_HUMAN, 0, false);
    this.ghostModel.texture = baked.texture;
    this.ghostModel.anchor.set(baked.anchorX, baked.anchorY);
    this.ghostModel.alpha = g.ok ? 0.55 : 0.32;
    this.ghostModel.tint = g.ok ? 0xffffff : 0xff8080;

    this.ghost.texture = tex;
    this.ghost.visible = true;
    this.ghostModel.visible = true;
    const cx = g.tx + fp / 2;
    const cy = g.ty + fp / 2;
    const wx = tileToWorldX(cx, cy);
    const wy = tileToWorldY(cx, cy);
    this.ghost.position.set(wx, wy);
    this.ghostModel.position.set(wx, wy);
  }

  // ── 框选 ────────────────────────────────────────────────

  drawSelectionBox(x0: number, y0: number, x1: number, y1: number): void {
    this.selectionBox.clear();
    const x = Math.min(x0, x1);
    const y = Math.min(y0, y1);
    const w = Math.abs(x1 - x0);
    const h = Math.abs(y1 - y0);
    if (w < 2 && h < 2) return;
    this.selectionBox.rect(x, y, w, h);
    this.selectionBox.fill({ color: 0x6fe89a, alpha: 0.12 });
    this.selectionBox.stroke({ color: 0x6fe89a, width: 1.5, alpha: 0.9 });
  }

  clearSelectionBox(): void {
    this.selectionBox.clear();
  }

  // ── 拾取 ────────────────────────────────────────────────

  screenToTile(sx: number, sy: number): { tx: number; ty: number } {
    const w = this.camera.screenToWorld(sx, sy);
    return {
      tx: Math.floor(worldToTileX(w.x, w.y)),
      ty: Math.floor(worldToTileY(w.x, w.y)),
    };
  }

  screenToTileF(sx: number, sy: number): { tx: number; ty: number } {
    const w = this.camera.screenToWorld(sx, sy);
    return { tx: worldToTileX(w.x, w.y), ty: worldToTileY(w.x, w.y) };
  }

  pickAt(sx: number, sy: number): number {
    const w = this.camera.screenToWorld(sx, sy);
    let best = -1;
    let bestY = -Infinity;
    for (const [id, v] of this.views) {
      if (v.kind === Kind.Projectile) continue;
      if (!v.revealed) continue;
      const def = defOf(v.typeId);
      const rw = def.kind === Kind.Building ? def.footprint * HALF_W * 0.8 : Math.max(16, def.radius * TILE_W * 0.7);
      const rh = def.kind === Kind.Building ? def.footprint * HALF_H * 0.8 : Math.max(12, def.radius * TILE_H * 0.9);
      const dx = (w.x - v.curX) / rw;
      const dy = (w.y - v.curY + rh * 0.35) / rh;
      if (dx * dx + dy * dy > 1) continue;
      if (v.curY > bestY) {
        bestY = v.curY;
        best = id;
      }
    }
    return best;
  }

  pickInBox(x0: number, y0: number, x1: number, y1: number): number[] {
    const a = this.camera.screenToWorld(Math.min(x0, x1), Math.min(y0, y1));
    const b = this.camera.screenToWorld(Math.max(x0, x1), Math.max(y0, y1));
    const out: number[] = [];
    for (const [id, v] of this.views) {
      if (v.owner !== PLAYER_HUMAN) continue;
      if (v.kind === Kind.Projectile || v.kind === Kind.Building) continue;
      if (v.curX < a.x || v.curX > b.x || v.curY < a.y || v.curY > b.y) continue;
      out.push(id);
    }
    return out;
  }

  viewOf(id: number): { owner: number; typeId: number; hp: number; maxHp: number; x: number; y: number; flags: number } | null {
    const v = this.views.get(id);
    if (!v) return null;
    return { owner: v.owner, typeId: v.typeId, hp: v.hp, maxHp: v.maxHp, x: v.curX, y: v.curY, flags: v.flags };
  }

  *unitsOf(owner: number): Generator<{ id: number; typeId: number }> {
    for (const [id, v] of this.views) {
      if (v.owner !== owner) continue;
      if (v.kind === Kind.Building || v.kind === Kind.Projectile) continue;
      yield { id, typeId: v.typeId };
    }
  }

  *entityBlips(): Generator<{ tx: number; ty: number; owner: number; kind: number }> {
    for (const v of this.views.values()) {
      if (v.kind === Kind.Projectile) continue;
      if (!v.revealed) continue;
      yield {
        tx: worldToTileX(v.curX, v.curY),
        ty: worldToTileY(v.curX, v.curY),
        owner: v.owner,
        kind: v.kind,
      };
    }
  }

  get fogThumbnail(): HTMLCanvasElement {
    return this.fogCanvas;
  }

  viewportCorners(): Array<{ tx: number; ty: number }> {
    const c = this.camera;
    const pts = [
      [0, 0],
      [c.viewW, 0],
      [c.viewW, c.viewH],
      [0, c.viewH],
    ];
    return pts.map(([sx, sy]) => {
      const w = c.screenToWorld(sx!, sy!);
      return { tx: worldToTileX(w.x, w.y), ty: worldToTileY(w.x, w.y) };
    });
  }

  centerOnTile(tx: number, ty: number): void {
    this.camera.centerOn(tileToWorldX(tx, ty), tileToWorldY(tx, ty));
  }
}
